import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { StepService } from '../shared/steps/step.service'
import { LocalStorageStepData, SelectionTypeEnum, IFlowReservation } from '../shared/steps/step.model'
import { QuoteChanged, PriceSpecificationType, QuoteLine, UserQuoteDetails } from '../shared/quote/quote.events'
import { flatMap, toParamsObj } from '../shared/utils'
import { ResourceService } from '../shared/resources/resource.service'
import { ITpaModel, ITpaItemSelection, ITpaResult, ITpaCategory, ITpaGroupItem, ITpaItem, ILegDates } from 'app/tpa/tpa.interfaces'
import { TpaLeg } from 'app/tpa/tpa.enums'
import { SalesChannelService } from 'app/shared/salesChannel/sales-channel.service'
import 'app/shared/extensions/array.extensions.ts'
import { AuthHttpService } from 'app/shared/auth-http.service';
import { EnvironmentService } from 'app/environment.service';
import { NoVehicleId } from 'app/shared';

@Injectable()
export class TpaService {
    private step: any

    constructor(private http: HttpClient, private authhttp: AuthHttpService, private resourceService: ResourceService, private salesChannelService: SalesChannelService, private stepService: StepService, private enviromentService: EnvironmentService) { }

    public getTpaAmendment(reservationCode: string): Observable<ITpaModel> {
        const url = `${this.enviromentService.getSbServiceUrl()}/api/tpa-amendment/`
        let params = { 'reservationCode': reservationCode }
        return this.authhttp.get<ITpaModel>(url, { params: toParamsObj(params) })
    }

    public getTpaFlow(): Observable<ITpaModel> {
        const reservation = this.stepService.combineToReservation()
        let params = {
            'outboundDepartureId': reservation.outboundDepartureId,
            'returnDepartureId': reservation.returnDepartureId,
            'productCode': reservation.productCode,
            'outboundRouteCode': reservation.routeCode,
            'salesOwnerId': this.resourceService.getSalesOwner(),
            'localeCode': this.resourceService.getLocale(),
            'salesChannelCode': this.salesChannelService.getCode(),
            'adults': reservation.adults,
            'children': reservation.children,
            'infants': reservation.infants,
            'leadVehicles': this.numberOfVehicles(reservation)
        }
        const url = `${this.enviromentService.getSbServiceUrl()}/api/tpa-flow/`
        return this.http.get<ITpaModel>(url, { params: toParamsObj(params) })
    }

    public postHybridTpa(selections: ITpaItemSelection[]): Observable<ITpaResult> {
        this.updateStepInLocalStorage(selections)
        return this.http.post<ITpaResult>('/_mvc/booking/tpa-flow', selections)
    }

    public getSelections(tpaCategories: ITpaCategory[]): ITpaItemSelection[] {
        return tpaCategories
            .flatMap<ITpaCategory, ITpaGroupItem>(x => x.tpaGroupItems)
            .flatMap<ITpaGroupItem, ITpaItem>(x => x.tpaItems)
            .map(x => ({
                id: x.id,
                tpaType: x.tpaType,
                count: Number(x.selectedCount),
                optionId: x.selectedOption === '0' ? null : x.selectedOption
            }))
    }

    public updateIsOpen(tpaCategories: ITpaCategory[]) {
        tpaCategories.forEach(category => {
            category.isOpen = category.tpaGroupItems.some(g => g.tpaItems.some(i => parseInt(i.selectedCount) > 0))
        });
    }

    public getQuoteModel(tpaCategories: ITpaCategory[], tpaItemSelection: ITpaItemSelection[], currencyCode: string): QuoteChanged {
        return new QuoteChanged(PriceSpecificationType.Tpa,
            this.getQuoteData(tpaCategories, tpaItemSelection, TpaLeg.Out, currencyCode),
            this.getQuoteData(tpaCategories, tpaItemSelection, TpaLeg.Return, currencyCode),
            this.getQuoteData(tpaCategories, tpaItemSelection, TpaLeg.Extra, currencyCode)
        )
    }

    public setSelectionsOnViewModel(tpaCategories: ITpaCategory[], selections: ITpaItemSelection[] = []) {
        tpaCategories
            .flatMap<ITpaCategory, ITpaGroupItem>(c => c.tpaGroupItems)
            .flatMap<ITpaGroupItem, ITpaItem>(c => c.tpaItems)
            .forEach(c => {
                let tpaItem: ITpaItemSelection = selections.find(x => x.id === c.id && x.tpaType === c.tpaType)

                c.selectedCount = tpaItem ? tpaItem.count.toString() : c.minNoOrder.toString()
                c.selectedOption = tpaItem ? tpaItem.optionId : null
            })
    }

    private getQuoteData(tpaCategories: ITpaCategory[], tpaItemSelection: ITpaItemSelection[], leg: TpaLeg, currencyCode: string): UserQuoteDetails {
        return new UserQuoteDetails(tpaItemSelection.map(
            s => tpaCategories
                .flatMap<ITpaCategory, ITpaGroupItem>(c => c.tpaGroupItems)
                .filter(c => c.tpaLeg === leg)
                .flatMap<ITpaGroupItem, ITpaItem>(c => c.tpaItems)
                .filter(c => s.id === c.id && s.count !== 0)
                .map(c => new QuoteLine(c.name, c.price, currencyCode, s.count))
        )
            .reduce((a, b) => a.concat(b), []))
    }

    private updateStepInLocalStorage(selections: ITpaItemSelection[]) {
        let step: any = {}
        step.tpaSelections = new LocalStorageStepData(SelectionTypeEnum.User, selections)
        this.stepService.saveStepData(step, window.location.pathname, false)
    }

    private numberOfVehicles(reservation: IFlowReservation) {
        return reservation.vehicleAndTrailerTypes.filter(t => t.vehicleId != NoVehicleId).length;
    }

}
